package com.hyperether.goodjob.repository

import androidx.compose.ui.graphics.painter.BitmapPainter
import com.hyperether.goodjob.database.ContactDao
import com.hyperether.goodjob.database.JobDao
import com.hyperether.goodjob.database.PlanDao
import com.hyperether.goodjob.database.SiteDao
import com.hyperether.goodjob.database.SkillsDao
import com.hyperether.goodjob.database.TeamDaoImpl
import com.hyperether.goodjob.database.UserDao
import com.hyperether.goodjob.database.WorkspaceDao
import com.hyperether.goodjob.managers.FileManager
import com.hyperether.goodjob.models.AddressObject
import com.hyperether.goodjob.models.CompletionReportStatus
import com.hyperether.goodjob.models.Contact
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.JobStatus
import com.hyperether.goodjob.models.Plan
import com.hyperether.goodjob.models.Repeat
import com.hyperether.goodjob.models.Role
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.models.Skills
import com.hyperether.goodjob.models.Step
import com.hyperether.goodjob.models.SuperAdmin
import com.hyperether.goodjob.models.Team
import com.hyperether.goodjob.models.User
import com.hyperether.goodjob.models.UserStatus
import com.hyperether.goodjob.models.Workspace
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.repository.remote.GoodJobApiImpl
import com.hyperether.goodjob.repository.remote.auth.AppleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.GoogleLoginRequest
import com.hyperether.goodjob.repository.remote.auth.MicrosoftLoginRequest
import com.hyperether.goodjob.repository.remote.model.AllJobReportsRequest
import com.hyperether.goodjob.repository.remote.model.AllUsersByRoleRequest
import com.hyperether.goodjob.repository.remote.model.CompletionReportRequest
import com.hyperether.goodjob.repository.remote.model.CompletionReportResponse
import com.hyperether.goodjob.repository.remote.model.ConfirmationResponse
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyRequest
import com.hyperether.goodjob.repository.remote.model.EphemeralKeyResponse
import com.hyperether.goodjob.repository.remote.model.FileObject
import com.hyperether.goodjob.repository.remote.model.FileResult
import com.hyperether.goodjob.repository.remote.model.FinishJobRequest
import com.hyperether.goodjob.repository.remote.model.FinishJobResponse
import com.hyperether.goodjob.repository.remote.model.ForgotPasswordRequest
import com.hyperether.goodjob.repository.remote.model.ForgotPasswordResponse
import com.hyperether.goodjob.repository.remote.model.JobRequest
import com.hyperether.goodjob.repository.remote.model.JobStepsUpdateRequest
import com.hyperether.goodjob.repository.remote.model.LoginResponse
import com.hyperether.goodjob.repository.remote.model.MessageResponse
import com.hyperether.goodjob.repository.remote.model.ParsedAddress
import com.hyperether.goodjob.repository.remote.model.ResetPasswordRequest
import com.hyperether.goodjob.repository.remote.model.Resource
import com.hyperether.goodjob.repository.remote.model.SetPasswordRequest
import com.hyperether.goodjob.repository.remote.model.SetPasswordResponse
import com.hyperether.goodjob.repository.remote.model.SetupPaymentIntentResponse
import com.hyperether.goodjob.repository.remote.model.SiteRequest
import com.hyperether.goodjob.repository.remote.model.SkillRequest
import com.hyperether.goodjob.repository.remote.model.SubscribeToPlanResponse
import com.hyperether.goodjob.repository.remote.model.TeamRequest
import com.hyperether.goodjob.repository.remote.model.TeamsByWorkspaceRequest
import com.hyperether.goodjob.repository.remote.model.UploadFileResponse
import com.hyperether.goodjob.repository.remote.model.UserRequest
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.file_deleted
import com.hyperether.goodjob.resources.no_data_received
import com.hyperether.goodjob.resources.unexpected_error
import com.hyperether.goodjob.resources.unknown_error
import com.hyperether.goodjob.resources.workspace_not_found
import com.hyperether.goodjob.util.ContactUtil
import com.hyperether.goodjob.util.FileUtil
import com.hyperether.goodjob.util.LocationUtil
import com.hyperether.goodjob.util.Logger
import com.hyperether.resources.stringResourcePlain
import com.mmk.kmpnotifier.notification.NotifierManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import org.lighthousegames.logging.logging

class RepositoryImpl(
    private val workspaceDao: WorkspaceDao,
    private val userDao: UserDao,
    private val planDao: PlanDao,
    private val jobDao: JobDao,
    private val contactDao: ContactDao,
    private val siteDao: SiteDao,
    private val skillsDao: SkillsDao,
    private val prefs: PrefsManager,
    private val goodJobApiImpl: GoodJobApiImpl,
    private val teamDao: TeamDaoImpl,
    private val fileManager: FileManager
) : Repository {
    val log = logging("RepositoryImpl")

    override suspend fun findPlace(name: String): List<ParsedAddress>? {
        return goodJobApiImpl.findPlace(name)?.map { LocationUtil.parseFormattedAddress(it) }
    }

    // *******************************  WORKSPACE *******************************

    override suspend fun deleteAllWorkspaces() {
        workspaceDao.deleteAllWorkspaces()
    }

    override suspend fun insertWorkspace(workspace: Workspace) {
        workspaceDao.insertWorkspace(workspace)
    }

    override suspend fun getWorkspacesFlow(): Flow<List<Workspace>> =
        workspaceDao.getWorkspacesFlow().map { workspaceList ->
            workspaceList.map { workspace ->
                Mapper().toWorkspace(workspace)
            }
        }

    override suspend fun getWorkspaceById(id: String): Workspace? {
        return workspaceDao.getWorkspaceById(id)?.let { Mapper().toWorkspace(it) }
    }

    override suspend fun addWorkspace(name: String, workspaceType: String): Resource<Workspace> {
        val response = goodJobApiImpl.addWorkspace(name, workspaceType, getToken())
        if (response.data != null) {
            insertWorkspace(response.data)
            addDefaultContact(response.data)
        }
        return response
    }

    override suspend fun deleteWorkspaceById(id: Long) {
        workspaceDao.deleteWorkspaceById(id)
    }

    override suspend fun updateWorkspaceLastAccessedById(
        lastAccessed: String, workspaceId: String
    ): Resource<Workspace> {
        return try {
            val workspace = getWorkspaceById(workspaceId)
            workspace?.let {
                workspace.lastAccessed = lastAccessed
                val request = Mapper().toUpdateWorkspaceRequest(workspace)
                val apiResponse = goodJobApiImpl.updateWorkspace(request, workspaceId, getToken())
                when (apiResponse) {
                    is Resource.Success -> {
                        val workspaceResponse = apiResponse.data
                        workspaceResponse?.let { it1 -> workspaceDao.updateWorkspaceById(it1) }
                        Resource.Success(workspaceResponse)
                    }

                    is Resource.Error -> {
                        log.d("updateWorkspaceLastAccessedById") {
                            "Failed to update workspace: $workspaceId, error: ${apiResponse.text}"
                        }
                        Resource.Error(apiResponse.text)
                    }
                }
            } ?: run {
                log.d("updateWorkspaceLastAccessedById") { "Workspace with ID $workspaceId not found" }
                Resource.Error(stringResourcePlain(Res.string.workspace_not_found) + " $workspaceId")
            }
        } catch (e: Exception) {
            log.d("updateWorkspaceLastAccessedById") {
                "An unexpected error occurred: ${e.message}"
            }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun addDefaultContact(workspace: Workspace?) {
        workspace?.let {
            val user = getUser()
            val contact = Contact(
                name = workspace.name,
                contactType = "",
                industry = "",
                addressObject = AddressObject(
                    address = user?.address,
                    city = user?.city,
                    country = user?.country,
                    zip = user?.zip
                ),
                email = user?.email,
                phone = user?.phone,
                personName = user?.fullName,
                personEmail = user?.email,
                personPhone = user?.phone,
                workspaceId = workspace._id
            )
            addContact(contact)
        }
    }

    override suspend fun getUserWorkspaces(): Resource<List<Workspace>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllWorkspacesId(getToken())) {
                is Resource.Success -> {
                    val workspaceResponse = apiResponse.data
                    val localWorkspacesList = workspaceDao.getWorkspacesFlow()
                    val localWorkspacesMap =
                        localWorkspacesList.firstOrNull()?.associateBy { it._id }
                    workspaceResponse?.forEach { workspace ->
                        try {
                            val remoteId = workspace._id
                            if (remoteId.isNullOrBlank()) {
                                log.d("Workspace Sync Error") { "Skipping workspace with null or blank ID: ${workspace.name}" }
                                return@forEach
                            }

                            val existingWorkspace = localWorkspacesMap?.get(remoteId)
                            if (existingWorkspace != null) {
                                workspaceDao.updateWorkspaceById(workspace)
                            } else {
                                workspaceDao.insertWorkspace(workspace)
                            }
                        } catch (e: Exception) {
                            log.d("Workspace Sync Error") {
                                "Failed to sync workspace: ${workspace._id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(workspaceResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllWorkspaces(): Resource<List<Workspace>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllWorkspaces(getToken())) {
                is Resource.Success -> {
                    val workspaceResponse = apiResponse.data?.toMutableList()
                    val localWorkspacesList = workspaceDao.getWorkspacesFlow()
                    val localWorkspacesMap =
                        localWorkspacesList.firstOrNull()?.associateBy { it._id }
                    workspaceResponse?.forEach { workspace ->
                        try {
                            val remoteId = workspace._id
                            if (remoteId.isNullOrBlank()) {
                                log.d("Workspace Sync Error") { "Skipping workspace with null or blank ID: ${workspace.name}" }
                                return@forEach
                            }

                            val existingWorkspace = localWorkspacesMap?.get(remoteId)
                            if (existingWorkspace != null) {
                                workspaceDao.updateWorkspaceById(workspace)
                            } else {
                                workspaceDao.insertWorkspace(workspace)
                            }
                        } catch (e: Exception) {
                            log.d("Workspace Sync Error") {
                                "Failed to sync workspace: ${workspace._id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(workspaceResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    // *******************************  PLAN *******************************

    override suspend fun getAllPlans(): Resource<List<Plan>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllPlans(getToken())) {
                is Resource.Success -> {
                    val remotePlanList = apiResponse.data
                    if (remotePlanList?.isNotEmpty() == true) {
                        val localPlansList = planDao.getPlans()
                        val localPlansMap = localPlansList.associateBy { it._id }
                        val remotePlanMap = remotePlanList.associateBy { it._id }
                        remotePlanList.forEach { plan ->
                            try {
                                val remoteId = plan._id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("" + "Plans Sync Error") { "${plan._id}" }
                                    return@forEach
                                }

                                val existingContact = localPlansMap[remoteId]
                                if (existingContact != null) {
                                    planDao.updatePlanById(plan)
                                } else {
                                    planDao.insertPlan(plan)
                                }
                            } catch (e: Exception) {
                                log.d("Plan Sync Error") {
                                    "Failed to sync plan: ${plan._id}, error: ${e.message}"
                                }
                            }
                        }
                        localPlansList.forEach {
                            val remoteExistInLocalDb = remotePlanMap[it._id]
                            if (remoteExistInLocalDb == null) {
                                it._id?.let { localId ->
                                    planDao.deletePlanById(localId)
                                }
                            }
                        }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getPlansFlow(): Flow<List<Plan>> = planDao.getPlansFlow().map { planList ->
        planList
            .map { plan -> Mapper().toPlan(plan) }
            .distinctBy { it._id }
    }

    override suspend fun getPlan(id: String): Plan? {
        return planDao.getPlanById(id)?.let { Mapper().toPlan(it) }
    }

    override suspend fun deletePlanById(id: String) {
        planDao.deletePlanById(id)
    }

    // *******************************  USER *******************************

    override suspend fun getUsersFlow(): Flow<List<User>> = userDao.getUsersFlow().map {
        it.map { user -> Mapper().toUser(user) }
    }

    override suspend fun getUsers(): Resource<List<User>> {
        return try {
            val apiResponse = goodJobApiImpl.getAllUsersByRole(
                getToken(), AllUsersByRoleRequest(prefs.getWorkspaceId())
            )

            when (apiResponse) {
                is Resource.Success -> {
                    val remoteList = apiResponse.data
                    if (remoteList?.isNotEmpty() == true) {
                        val localList = userDao.getUsers()
                        val localMap = localList.associateBy { it._id }
                        val remoteMap = remoteList.associateBy { it._id }
                        val currentUser = getUser()
                        remoteList.forEach { user ->
                            try {
                                val remoteId = user._id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("Users Sync Error") { "Skipping contact with null or blank ID: ${user.getName()}" }
                                    return@forEach
                                }

                                if (remoteId == currentUser?._id) {
                                    // Update logged user in shared prefs
                                    saveUser(user)
                                }

                                val existingUser = localMap[remoteId]
                                if (existingUser != null) {
                                    val localUserModel = Mapper().toUser(existingUser)
                                    if (localUserModel.isDifferent(user)) {
                                        userDao.updateUserById(user)
                                    }
                                } else {
                                    userDao.insertUser(user)
                                }
                            } catch (e: Exception) {
                                log.d("Users Sync Error") {
                                    "Failed to sync users: ${user._id}, error: ${e.message}"
                                }
                            }
                        }

                        localList.forEach {
                            val remoteExistInLocalDb = remoteMap[it._id]
                            if (remoteExistInLocalDb == null) {
                                it._id?.let { localId ->
                                    userDao.deleteUserById(localId)
                                }
                            }
                        }
                    } else {
                        userDao.deleteAllUsers()
                    }
                    Resource.Success(remoteList)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun confirmation(hash: String): Resource<ConfirmationResponse> {
        val result = goodJobApiImpl.confirmation(hash)
        result.data?.let {
            prefs.saveToken(it.token ?: "")
            prefs.saveRefreshToken(it.refreshToken ?: "")
            saveUser(it.data)
        }
        return result
    }

    override suspend fun resendConfirmationEmail(email: String): Resource<String> {
        return goodJobApiImpl.resendConfirmationEmail(email)
    }

    override suspend fun addUser(
        userRequest: UserRequest
    ): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.addUser(getToken(), userRequest)) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    user?.let { userDao.insertUser(it) }
                    checkRestrictions()
                    Resource.Success(user)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateUser(
        userId: String, userRequest: UserRequest
    ): Resource<User> {
        return try {
            val id = if (userId.contains("profileSettings")) {
                userId.substringAfter("-")
            } else {
                userId
            }
            val apiResponse = goodJobApiImpl.updateUserById(
                userId = id, token = getToken(), addUserRequest = userRequest
            )
            when (apiResponse) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    if (user != null) {
                        val localList = userDao.getUsers()
                        val localMap = localList.associateBy { it._id }
                        try {
                            val remoteId = user._id
                            if (!remoteId.isNullOrBlank()) {
                                if (userId.contains("profileSettings")) {
                                    saveUser(user)
                                    getUsers()
                                } else {
                                    val existingItem = localMap[remoteId]
                                    if (existingItem != null) {
                                        val localUserModel = Mapper().toUser(existingItem)
                                        if (localUserModel != user) {
                                            userDao.updateUserById(user)
                                        }
                                    }
                                }
                            }
                        } catch (e: Exception) {
                            log.d("updateUser Error") {
                                "Failed to update user: ${user._id}, error: ${e.message}"
                            }
                        }
                    }
                    checkRestrictions()
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteUser(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteUser(getToken(), id)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        userDao.deleteUserById(id)
                    }
                    checkRestrictions()
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getUserById(id: String): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.getUserById(getToken(), id)) {
                is Resource.Success -> {
                    val user = apiResponse.data
                    log.d("getUserById") { "getUserById SUCCESS $user" }
                    Resource.Success(user)
                }

                is Resource.Error -> {
                    log.d("getUserById") { "getUserById ERROR ${apiResponse.text}" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.d("getUserById") { "getUserById EXCEPTION ${e.message}" }
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun subscribeUser(email: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.subscribeUser(email)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override fun saveUser(user: User) {
        prefs.saveUser(user)
    }

    override fun getUser(): User? {
        return prefs.getUser()
    }

    override suspend fun logoutUser() {
        NotifierManager.getLocalNotifier().removeAll()
        clearPushToken()
        deleteAllWorkspaces()
        userDao.deleteAllUsers()
        jobDao.deleteAllJobs()
        siteDao.deleteAllSites()
        contactDao.deleteAllContacts()
        teamDao.deleteAllTeams()
        deleteAllSkills()
        prefs.saveEmail("")
        prefs.saveToken("")
        prefs.saveRefreshToken("")
        prefs.saveUser(null)
        prefs.saveWorkspaceId("")
    }

// ***************** CONTACT *****************

    override suspend fun getContactsFlow(): Flow<List<Contact>> =
        contactDao.getContactsFlow().map { list ->
            list.map {
                Mapper().toContact(it)
            }
        }

    override suspend fun getContact(id: String): Contact? {
        return contactDao.getContactById(id)?.let {
            Mapper().toContact(it)
        }
    }

    override suspend fun addContact(contact: Contact): Resource<Contact> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.addContact(getToken(), Mapper().toContactRequest(contact))) {
                is Resource.Success -> {
                    val contactResponse = apiResponse.data
                    if (contactResponse != null) {
                        contactDao.insertContact(contactResponse)
                        addDefaultSite(contactResponse)
                    }

                    Resource.Success(contactResponse)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateContact(
        contact: Contact
    ): Resource<Contact> {
        return try {
            val contactId =
                contact.id ?: throw IllegalArgumentException("Contact ID cannot be null")

            // find default site:
            val siteId = contactDao.getContactById(contactId)?.let { contactDb ->
                siteDao.getSitesByContactId(contactId)
                    .map { Mapper().toSite(it) }
                    .find {
                        ContactUtil.isSiteCreatedFromContact(
                            it,
                            Mapper().toContact(contactDb)
                        )
                    }
                    ?.id
            }
            when (val apiResponse = goodJobApiImpl.updateContact(
                contactId,
                getToken(),
                Mapper().toContactRequest(contact)
            )) {
                is Resource.Success -> {
                    val contactResponse = apiResponse.data
                    if (contactResponse != null) {
                        val localContactList = contactDao.getContacts()
                        val localContactsMap = localContactList.associateBy { it._id }
                        try {
                            val remoteId = contactResponse.id
                            if (!remoteId.isNullOrBlank()) {
                                val existingContact = localContactsMap[remoteId]
                                if (existingContact != null) {
                                    contactDao.updateContactById(contactResponse)
                                    siteId?.let { it1 ->
                                        updateSite(
                                            it1,
                                            contactResponse.getDefaultSite()
                                        )
                                    }
                                }
                            }
                        } catch (e: Exception) {
                            log.d("updateContact Error") {
                                "Failed to updateContact: ${contactResponse.id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteContactById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteContact(getToken(), id)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        contactDao.deleteContactById(id)
                        val sites = siteDao.getSitesByContactId(id)
                        sites.forEach {
                            it._id?.let { siteId ->
                                siteDao.deleteSiteById(siteId)
                            }
                        }
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllContactsByWorkspace(): Resource<List<Contact>> {
        return try {
            val apiResponse = goodJobApiImpl.getAllContactsByWorkspace(
                getWorkspaceId(), getToken()
            )

            when (apiResponse) {
                is Resource.Success -> {
                    val remoteList = apiResponse.data
                    if (remoteList?.isNotEmpty() == true) {
                        val localList = contactDao.getContacts()
                        val localMap = localList.associateBy { it._id }
                        val remoteMap = remoteList.associateBy { it.id }
                        IndustryManager.setIndustriesFromContacts(remoteList)
                        remoteList.forEach { contact ->
                            try {
                                val remoteId = contact.id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("Contact Sync Error") { "Skipping contact with null or blank ID: ${contact.name}" }
                                    return@forEach
                                }

                                CoroutineScope(Dispatchers.Default).launch {
                                    getSitesForContactRemote(remoteId)
                                }

                                val existingContact = localMap[remoteId]
                                if (existingContact != null) {
                                    contactDao.updateContactById(contact)
                                } else {
                                    contactDao.insertContact(contact)
                                }
                            } catch (e: Exception) {
                                log.d("Contact Sync Error") {
                                    "Failed to sync contact: ${contact.id}, error: ${e.message}"
                                }
                            }
                        }

                        localList.forEach {
                            val remoteExistInLocalDb = remoteMap[it._id]
                            if (remoteExistInLocalDb == null) {
                                it._id?.let { localId ->
                                    contactDao.deleteContactById(localId)
                                }
                            }
                        }
                    } else {
                        contactDao.deleteAllContacts()
                    }
                    Resource.Success(remoteList)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    suspend fun addDefaultSite(contact: Contact) {
        addSite(contact.getDefaultSite())
    }

    override suspend fun addSite(site: Site): Resource<Site> {
        return try {
            when (val apiResponse = goodJobApiImpl.addSite(getToken(), site)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        siteDao.insertSite(response)
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteSiteById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteSiteById(getToken(), id)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        siteDao.deleteSiteById(id)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateSite(siteId: String, site: Site): Resource<Site> {
        return try {
            when (val apiResponse = goodJobApiImpl.updateSite(getToken(), siteId, site)) {
                is Resource.Success -> {
                    val siteResponse = apiResponse.data
                    if (siteResponse != null) {
                        val localSitesList = siteDao.getSites()
                        val localSitesMap = localSitesList?.associateBy { it._id }
                        try {
                            val remoteId = siteResponse.id
                            if (!remoteId.isNullOrBlank()) {
                                val existingSite = localSitesMap?.get(remoteId)
                                if (existingSite != null) {
                                    val localSite = Mapper().toSite(existingSite)
                                    if (localSite.isDifferent(siteResponse))
                                        siteDao.updateSite(siteResponse)
                                }
                            }
                        } catch (e: Exception) {
                            log.d("updateSite Error") {
                                "Failed to updateSite: ${siteResponse.id}, error: ${e.message}"
                            }
                        }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getSitesFlow(): Flow<List<Site>> {
        return siteDao.getSitesFlow().map {
            it.map { site -> Mapper().toSite(site) }
        }
    }

    override suspend fun getSiteById(id: String): Site? {
        return siteDao.getSiteById(id)?.let {
            Mapper().toSite(it)
        }
    }

    override suspend fun getSitesByContactId(contactId: String): List<Site> {
        return siteDao.getSitesByContactId(contactId).let {
            it.map { site -> Mapper().toSite(site) }
        }
    }

    override suspend fun getSitesByContactIdFlow(contactId: String): Flow<List<Site>> {
        return siteDao.getSitesByContactIdFlow(contactId).map {
            it.map { site -> Mapper().toSite(site) }
        }
    }

    override suspend fun getSitesForContactRemote(contactId: String): Resource<List<Site>> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getSitesForContact(getToken(), SiteRequest(contactId))) {
                is Resource.Success -> {
                    val sites = apiResponse.data
                    val remoteSitesMap = sites?.associateBy { it.id }
                    val localSites = siteDao.getSitesByContactId(contactId)
                    sites?.forEach { s ->
                        if (localSites.isEmpty()) {
                            siteDao.insertSite(s)
                        } else if (localSites.none { it._id == s.id }) {
                            siteDao.insertSite(s)
                        } else {
                            val localSiteDb = localSites.first { it._id == s.id }
                            val localSite = Mapper().toSite(localSiteDb)
                            if (s.isDifferent(localSite))
                                siteDao.updateSite(s)
                        }
                    }
                    localSites.forEach {
                        val remoteSiteExistInLocalDb = remoteSitesMap?.get(it._id)
                        if (remoteSiteExistInLocalDb == null) {
                            it._id?.let { siteId ->
                                siteDao.deleteSiteById(siteId)
                            }
                        }
                    }
                    Resource.Success(sites)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

// ********************* JOB *********************

    override suspend fun createJob(jobRequest: JobRequest): Resource<Job> {
        return try {
            when (val apiResponse = goodJobApiImpl.createJob(getToken(), jobRequest)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        jobDao.insertJob(response)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJob(
        jobId: String, jobRequest: JobRequest
    ): Resource<Job> {
        return try {
            jobRequest.apply {
                repeatInterval =
                    repeatInterval.takeIf { repeat != Repeat.never.name } ?: emptyList()
                dueDate = dueDate.takeIf { repeat != Repeat.never.name } ?: ""
            }
            when (val apiResponse = goodJobApiImpl.updateJob(getToken(), jobId, jobRequest)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        response.completionReport = jobDao.getJobById(jobId)?.completionReport
                        jobDao.updateJobById(response)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getJobById(jobId: String): Resource<Job> {
        return goodJobApiImpl.getJobById(jobId, getToken())
    }

    override suspend fun getAllJobsForWorkspace(): Resource<List<Job>> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getAllJobsForSpecificWorkspace(getToken(), getWorkspaceId())) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    processJobResponse(response)
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getAllJobsForUser(): Resource<List<Job>> {
        return try {
            when (val apiResponse = goodJobApiImpl.getAllJobsForSpecificUser(getToken())) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    processJobResponse(response)
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    private suspend fun processJobResponse(response: List<Job>?) {
        if (response?.isNotEmpty() == true) {
            val localJobList = jobDao.getJobsFromLocal()
            val localJobsMap = localJobList.associateBy { it._id }
            val remoteJobsMap = response.associateBy { it.id }

            response.forEach { job ->
                try {
                    val remoteId = job.id
                    if (remoteId.isNullOrBlank()) {
                        log.d("Job Sync Error") { "Skipping job with null or blank ID: ${job.jobTitle}" }
                        return@forEach
                    }

                    val existingJob = localJobsMap[remoteId]
                    if (existingJob != null) {
                        val localJob = Mapper().toJob(existingJob)
                        if (localJob.isJobDifferent(job)) {
                            job.completionReport = existingJob.completionReport
                            jobDao.updateJobById(job)
                        }
                    } else {
                        jobDao.insertJob(job)
                    }
                } catch (e: Exception) {
                    log.d("Job Sync Error") {
                        "Failed to sync Jobs: ${job.id}, error: ${e.message}"
                    }
                }
            }

            localJobList.forEach { job ->
                try {
                    if (job._id.isNullOrBlank()) {
                        log.d("Job Sync Error") { "Skipping job with null or blank ID: ${job.jobTitle}" }
                        return@forEach
                    }

                    val existingJob = remoteJobsMap[job._id]
                    if (existingJob == null) {
                        jobDao.deleteJobById(job._id)
                    }
                } catch (e: Exception) {
                    log.d("Job Sync Error") {
                        "Failed to sync job: ${job.id}, error: ${e.message}"
                    }
                }
            }

            getAllJobReports()
        }
    }

    override suspend fun getAllJobsForSpecificContact(contactParentId: String): Resource<List<Job>> {
        return goodJobApiImpl.getAllJobsForSpecificContact(
            contactParentId = contactParentId,
            token = getToken()
        )
    }

    override suspend fun getJobsFlow(): Flow<List<Job>> {
        return jobDao.getJobsFlow().map {
            log.d("getJobsFlow") { "data: $it" }
            it.map { job -> Mapper().toJob(job) }
        }
    }

    override suspend fun getJobByIdFlow(jobId: String): Flow<Job?> {
        return jobDao.getJobByIdFlow(jobId).map { job ->
            job?.let {
                Mapper().toJob(it)
            }
        }
    }

    override suspend fun getJobsByContactIdFlow(id: String): Flow<List<Job>> {
        return jobDao.getJobsByContactIdFlow(id).map {
            it.map { job -> Mapper().toJob(job) }
        }
    }

    override suspend fun getAllJobReports(): Resource<List<CompletionReportResponse>> {
        return try {
            val apiResponse =
                goodJobApiImpl.getAllJobReports(getToken(), AllJobReportsRequest(getWorkspaceId()))

            when (apiResponse) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response?.isNotEmpty() == true) {
                        response.forEach { report ->
                            val existingReport = jobDao.getJobCompletionReport(report.jobId)
                            if (existingReport == null || existingReport.isDifferent(report)) {
                                jobDao.updateJobCompletionReport(report.jobId, report)
                            }
                        }
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteJobById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteJobById(getToken(), id)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        jobDao.deleteJobById(id)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun submitCompletionReport(request: CompletionReportRequest): Resource<CompletionReportResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.submitCompletionReport(getToken(), request)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        when (response.status) {
                            CompletionReportStatus.pendingApproval.name,
                            CompletionReportStatus.rejected.name -> {
                                updateJobStatus(response.jobId, JobStatus.pendingApproval)
                            }

                            CompletionReportStatus.pending.name -> {

                            }

                            CompletionReportStatus.completed.name -> {
                                updateJobStatus(response.jobId, JobStatus.completed)
                            }
                        }
                        jobDao.updateJobCompletionReport(response.jobId, response)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getJobReportByJobId(jobId: String): Resource<CompletionReportResponse> {
        return try {
            val apiResponse = goodJobApiImpl.getJobReportByJobId(token = getToken(), jobId = jobId)
            when (apiResponse) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJobStatus(jobId: String, jobStatus: JobStatus) {
        val updatedJob = jobDao.getJobById(jobId)
        updatedJob?.let {
            it.status = jobStatus.name
            updateJob(jobId, Mapper().toJobUpdateRequest(it))
        }
    }

    override suspend fun finishJob(finishJobRequest: FinishJobRequest): Resource<FinishJobResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.finishJob(getToken(), finishJobRequest)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun updateJobStepsStatus(
        jobId: String, steps: List<Step>
    ): Resource<Job> {
        return try {
            when (val apiResponse = goodJobApiImpl.updateJobStepsStatus(
                getToken(), JobStepsUpdateRequest(jobId, steps)
            )) {
                is Resource.Success -> {
                    jobDao.updateJobSteps(jobId, steps)
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

//***************** SKILLS *********************************

    override suspend fun addSkill(skill: Skills): Resource<Skills> {
        return try {
            when (val apiResponse = goodJobApiImpl.addSkill(
                getToken(),
                getWorkspaceId(),
                SkillRequest(name = skill.name, workspaceId = getWorkspaceId())
            )) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    response?.let {
                        skillsDao.insertSkill(it)
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getSkillsFlow(): Flow<List<Skills>> {
        return skillsDao.getSkillsFlow(getWorkspaceId()).map {
            it.map { skill -> Mapper().toSkill(skill) }
        }
    }

    override suspend fun getSkillsByWorkspace(): Resource<List<Skills>> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getSkillsByWorkspace(getToken(), getWorkspaceId())) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response?.isNotEmpty() == true) {
                        val localList = skillsDao.getSkills(getWorkspaceId())
                        val localMap = localList.associateBy { it._id }
                        val remoteMap = response.associateBy { it._id }

                        response.forEach { skill ->
                            try {
                                val remoteId = skill._id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("getSkillsByWorkspace Error") { "Skipping skill with null or blank ID: ${skill.name}" }
                                    return@forEach
                                }

                                val existingContact = localMap[remoteId]
                                if (existingContact != null) {
                                    skillsDao.updateSkillById(skill)
                                } else {
                                    skillsDao.insertSkill(skill)
                                }
                            } catch (e: Exception) {
                                log.d("Contact Sync Error") {
                                    "Failed to sync contact: ${skill.id}, error: ${e.message}"
                                }
                            }
                        }

                        localList.forEach { item ->
                            try {
                                if (item._id.isNullOrBlank()) {
                                    log.d("Skill Sync Error") { "Skipping skill with null or blank ID: ${item.name}" }
                                    return@forEach
                                }

                                val existingItem = remoteMap[item._id]
                                if (existingItem == null) {
                                    skillsDao.deleteById(item._id)
                                }
                            } catch (e: Exception) {
                                log.d("Skill Sync Error") {
                                    "Failed to sync skill: ${item.id}, error: ${e.message}"
                                }
                            }
                        }
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteAllSkills() {
        log.d("deleteAllSkills") { "" }
        skillsDao.deleteAllSkills()
    }

//***************** TEAMS *********************************

    override suspend fun getTeamsFlow(): Flow<List<Team>> {
        return teamDao.getTeamsFlow().map {
            it.map { team -> Mapper().toTeam(team) }
        }
    }

    override suspend fun createTeam(name: String): Resource<Team> {
        return try {
            when (val apiResponse = goodJobApiImpl.createTeam(
                getToken(),
                teamRequest = TeamRequest(
                    name = name,
                    workspaceId = getWorkspaceId(),
                    employees = emptyList()
                )
            )) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        teamDao.insertTeam(response)
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun getTeams(): Resource<List<Team>> {
        return try {
            val apiResponse = goodJobApiImpl.getAllTeamsByWorkspace(
                getToken(),
                TeamsByWorkspaceRequest(getWorkspaceId())
            )

            when (apiResponse) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response?.isNotEmpty() == true) {
                        val localList = teamDao.getTeamsFromLocal()
                        val localMap = localList.associateBy { it._id }
                        val remoteMap = response.associateBy { it._id }

                        response.forEach { team ->
                            try {
                                val remoteId = team._id
                                if (remoteId.isNullOrBlank()) {
                                    log.d("getTeams Error") { "Skipping teams with null or blank ID: ${team.name}" }
                                    return@forEach
                                }

                                val existingTeam = localMap[remoteId]
                                if (existingTeam != null) {
                                    teamDao.updateTeamById(team)
                                } else {
                                    teamDao.insertTeam(team)
                                }
                            } catch (e: Exception) {
                                log.d("Team Sync Error") {
                                    "Failed to sync team: ${team._id}, error: ${e.message}"
                                }
                            }
                        }

                        localList.forEach { team ->
                            try {
                                if (team._id.isNullOrBlank()) {
                                    log.d("Team Sync Error") { "Skipping team with null or blank ID: ${team.name}" }
                                    return@forEach
                                }

                                val existingItem = remoteMap[team._id]
                                if (existingItem == null) {
                                    teamDao.deleteTeamById(team._id)
                                }
                            } catch (e: Exception) {
                                log.d("Team Sync Error") {
                                    "Failed to sync team: ${team.id}, error: ${e.message}"
                                }
                            }
                        }
                    }
                    Resource.Success(response)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteTeamById(id: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteTeam(getToken(), id)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        teamDao.deleteTeamById(id)
                    }

                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

//***************** LOGIN *********************************

    override suspend fun register(
        name: String, email: String, password: String
    ): Resource<User> {
        return try {
            when (val apiResponse = goodJobApiImpl.register(name, email, password)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("register") { "Register SUCCESS ${data.email}" }
                        saveUser(data)
                        Resource.Success(data)
                    } else {
                        log.d("register") { "Register ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Register An error occurred" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "Register Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun login(email: String, password: String): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.login(email, password)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.e { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginGoogle(request: GoogleLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginGoogle(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.e { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginApple(request: AppleLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginApple(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.e { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun loginMicrosoft(request: MicrosoftLoginRequest): Resource<LoginResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.loginMicrosoft(request)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("login") { "Login SUCCESS ${data.user.email}" }
                        saveToken(data.token)
                        saveRefreshToken(data.refreshToken)
                        saveUser(data.user)
                        Resource.Success(data)
                    } else {
                        log.e { "Login ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "Login ERROR An error occurred" }
                    val errorMessage = apiResponse.text ?: "Failed to login: ${apiResponse.text}"
                    val errorInternalCode = apiResponse.errorInternalCode
                    Resource.Error(errorMessage, errorInternalCode)
                }
            }
        } catch (e: Exception) {
            log.e { "Login Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun setWorkspace(
        workspaceId: String
    ): Resource<SuperAdmin> {
        return goodJobApiImpl.setWorkspace(workspaceId, getToken())
    }

    override suspend fun forgotPassword(forgotPasswordRequest: ForgotPasswordRequest): Resource<ForgotPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.forgotPassword(forgotPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("forgotPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("forgotPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "forgotPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun resetPassword(resetPasswordRequest: ResetPasswordRequest): Resource<ForgotPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.resetPassword(resetPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("resetPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("resetPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "resetPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun setPassword(setPasswordRequest: SetPasswordRequest): Resource<SetPasswordResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.setPassword(setPasswordRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("setPassword") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("setPassword") { "ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "setPassword Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun getEphemeralKey(ephemeralKeyRequest: EphemeralKeyRequest): Resource<EphemeralKeyResponse> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.getEphemeralKey(getToken(), ephemeralKeyRequest)) {
                is Resource.Success -> {
                    val data = apiResponse.data

                    if (data != null) {
                        log.d("getEphemeralKey") { "SUCCESS $data" }
                        Resource.Success(data)
                    } else {
                        log.d("getEphemeralKey") { "Register ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "getEphemeralKey Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun cancelSubscription(subscriptionId: String): Resource<MessageResponse> {
        return try {
            when (val apiResponse = goodJobApiImpl.cancelSubscription(getToken(), subscriptionId)) {
                is Resource.Success -> {
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun setupPaymentIntent(
        paymentMethods: List<String>,
        customerId: String
    ): Resource<SetupPaymentIntentResponse> {
        return goodJobApiImpl.setupPaymentIntent(getToken(), paymentMethods, customerId)
    }

    override suspend fun subscribeToPlan(
        planId: String,
        customerId: String
    ): Resource<SubscribeToPlanResponse> {
        return try {
            when (val apiResponse =
                goodJobApiImpl.subscribeToPlan(getToken(), planId, customerId)) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("subscribeToPlan") { "subscribeToPlan SUCCESS ${data.user?.planId}" }
                        data.user?.let { remoteUser ->
                            val localList = userDao.getUsers()
                            val localMap = localList.associateBy { it._id }
                            val existing = remoteUser._id?.let { localMap[it] }
                            if (existing == null) {
                                userDao.insertUser(remoteUser)
                            } else {
                                val localUserModel = Mapper().toUser(existing)
                                if (localUserModel != remoteUser) {
                                    userDao.updateUserById(remoteUser)
                                }
                            }
                            saveUser(remoteUser)
                        }
                        Resource.Success(data)
                    } else {
                        log.d("subscribeToPlan") { "subscribeToPlan ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "subscribeToPlan An error occurred" }
                    Resource.Error(
                        apiResponse.text ?: stringResourcePlain(Res.string.unknown_error)
                    )
                }
            }
        } catch (e: Exception) {
            log.e { "subscribeToPlan Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun checkRestrictions() {
        val users = getUsersFlow().first()
        val currentUser = getUser()
        val plan = getPlan(currentUser?.planId ?: return)

        val activeUsers = users.filter { it.status == UserStatus.active.name }
        val usersMap = activeUsers.groupBy { it.role }

        val managerCount = usersMap[Role.manager.name]?.size ?: 0
        val employeeCount = usersMap[Role.employee.name]?.size ?: 0

        setManagerNumberExceeded(managerCount > (plan?.limitManagers?.toInt() ?: Int.MAX_VALUE))
        setEmployeeNumberExceeded(employeeCount > (plan?.limitUsers?.toInt() ?: Int.MAX_VALUE))
    }

//***************** FILES *********************************

    override suspend fun getFile(fileId: String): Resource<FileResult> {
        return try {
            when (val apiResponse = goodJobApiImpl.getFile(fileId, getToken())) {
                is Resource.Success -> {
                    val data = apiResponse.data
                    if (data != null) {
                        log.d("getFile") { "getFile SUCCESS ${data.size}" }
                        val fileType = FileUtil().getFileType(fileId)
                        fileManager.saveFile(fileId, data)
                        Resource.Success(FileResult(data, fileType))
                    } else {
                        log.e { "getFile ERROR No data received" }
                        Resource.Error(stringResourcePlain(Res.string.no_data_received))
                    }
                }

                is Resource.Error -> {
                    log.e { "getFile ERROR An error occurred" }
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            log.e { "getFile Unexpected error occurred: ${e.printStackTrace()}" }
            Resource.Error(stringResourcePlain(Res.string.unexpected_error) + " ${e.printStackTrace()}")
        }
    }

    override suspend fun deleteFileFromJob(jobId: String, fileId: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteFile(getToken(), fileId)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        val existingJob = jobDao.getJobByIdFlow(jobId).firstOrNull()
                        if (existingJob != null) {
                            // Deserialize the  list
                            val files = existingJob.fileObjects?.let {
                                Mapper().jsonAsList<FileObject>(it)
                            } ?: emptyList()

                            // Remove the specified fileId
                            val updatedFiles = files.filterNot { it.documentFileId == fileId }
                            // Update the job object with the new files list
                            val updatedJob = existingJob.copy(
                                fileObjects = Mapper().listAsJson(updatedFiles)
                            )

                            // Make an API call to update the job
                            when (val updateJobResponse =
                                updatedJob.let { Mapper().toJobUpdateRequest(it) }.let {
                                    goodJobApiImpl.updateJob(
                                        getToken(), jobId, it
                                    )
                                }) {
                                is Resource.Success -> {
                                    fileManager.deleteFile(fileId)
                                    updateJobResponse.data?.let {
                                        it.completionReport =
                                            jobDao.getJobById(jobId)?.completionReport
                                        jobDao.updateJobById(it)
                                    }
                                    Resource.Success(stringResourcePlain(Res.string.file_deleted))
                                }

                                is Resource.Error -> {
                                    Resource.Error(updateJobResponse.text)
                                }
                            }
                        }
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun deleteFile(fileId: String): Resource<String> {
        return try {
            when (val apiResponse = goodJobApiImpl.deleteFile(getToken(), fileId)) {
                is Resource.Success -> {
                    val response = apiResponse.data
                    if (response != null) {
                        fileManager.deleteFile(fileId)
                    }
                    Resource.Success(apiResponse.data)
                }

                is Resource.Error -> {
                    Resource.Error(apiResponse.text)
                }
            }
        } catch (e: Exception) {
            Resource.Error(e.message ?: stringResourcePlain(Res.string.unknown_error))
        }
    }

    override suspend fun uploadFile(
        fileData: ByteArray, fileId: String
    ): Resource<UploadFileResponse> {
        return goodJobApiImpl.uploadFile(getToken(), fileData, fileId)
    }

    override fun getImage(imageFileId: String): BitmapPainter? {
        return fileManager.getBitmap(imageFileId)
    }

    override fun getImageFromByteArray(byteArray: ByteArray): BitmapPainter? {
        return fileManager.getBitmapFromByteArray(byteArray)
    }

    override fun getSavedFilePath(fileId: String): String? {
        return fileManager.getFilePath(fileId)
    }

    // PREFS
    override fun saveToken(token: String) {
        log.d("saveToken") { "token Saved" }
        prefs.saveToken(token)
    }

    override fun getToken() = prefs.getToken()
    override fun saveRefreshToken(refreshToken: String) {
        prefs.saveRefreshToken(refreshToken)
    }

    override fun getRefreshToken() = prefs.getRefreshToken()
    override fun saveEmail(email: String) {
        prefs.saveEmail(email)
    }

    override fun getEmail(): String {
        return prefs.getEmail()
    }

    override fun saveWorkspaceId(workspaceId: String) {
        prefs.saveWorkspaceId(workspaceId)
    }

    override fun getWorkspaceId(): String {
        return prefs.getWorkspaceId()
    }

    override fun setEmployeeNumberExceeded(isEmployeeNumberExceeded: Boolean) {
        prefs.setEmployeeNumberExceeded(isEmployeeNumberExceeded)
    }

    override fun isEmployeeNumberExceeded(): Boolean {
        return prefs.employeeNumberExceeded.value
    }

    override fun setManagerNumberExceeded(isManagerNumberExceeded: Boolean) {
        prefs.setManagerNumberExceeded(isManagerNumberExceeded)
    }

    override fun isManagerNumberExceeded(): Boolean {
        return prefs.managerNumberExceeded.value
    }

    override fun updateFCMPushToken(token: String) {
        prefs.prefs.putString("fcm_push_token", token)
    }

    override fun getWidQuery(): String {
        // used only for web
        return ""
    }

    override fun setWidQuery(wid: String) {
        // used only for web
        prefs.setWidQuery("")
    }

    override fun getFCMPushToken(): String {
        return prefs.prefs.getString("fcm_push_token")
    }

    private suspend fun clearPushToken() {
        getUser()?.let {
            it._id?.let { userId ->
                Logger.d("FCM", "clear token for ${it.email}")
                it.pushToken = ""
                updateUser(userId, Mapper().toUserRequest(it))
                Logger.d("FCM", "token cleared")
            }
        }
    }
}
